import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  MegaphoneIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../../common/LoadingSpinner";
import EibExpandRadiusPanel from "../EibExpandRadiusPanel";
import { getEventCampaignsWithAudiences } from "../../../services/eventCampaigns.service";

interface EventCampaignsTabProps {
  eventId: string;
}

function statusClasses(status: string): string {
  switch (status) {
    case "active":
    case "sent":
      return "bg-green-100 text-green-800";
    case "scheduled":
      return "bg-blue-100 text-blue-800";
    case "cancelled":
    case "failed":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export default function EventCampaignsTab({ eventId }: EventCampaignsTabProps) {
  const {
    data: campaigns = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["event-campaign-audiences", eventId],
    queryFn: () => getEventCampaignsWithAudiences(eventId),
    enabled: !!eventId,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading event campaigns…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Event campaigns and their audience segments could not be loaded. Please
        refresh and try again.
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-12">
        <MegaphoneIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No event campaigns yet
        </h3>
        <p className="text-gray-500">
          Invitation campaigns created through Event in a Box will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <EibExpandRadiusPanel eventId={eventId} />
      <div>
        <h3 className="text-lg font-semibold text-gray-900">
          Invitation campaigns
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Audience segments show the segment selected or created by Event in a
          Box. “Frozen audience” is the membership actually used for the send.
        </p>
      </div>

      {campaigns.map((campaign) => (
        <section
          key={campaign.id}
          className="rounded-lg border border-gray-200 bg-white p-5"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-semibold text-gray-900">{campaign.name}</h4>
                {campaign.isEib && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                    Event in a Box
                  </span>
                )}
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClasses(
                    campaign.status,
                  )}`}
                >
                  {campaign.status}
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {campaign.flightCount} flight
                {campaign.flightCount === 1 ? "" : "s"} · created{" "}
                {new Date(campaign.createdAt).toLocaleDateString()}
              </p>
            </div>
            <Link
              to={`/campaigns/${campaign.id}?backTo=${encodeURIComponent(
                `/events/${eventId}?tab=campaigns`,
              )}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-900"
            >
              View campaign
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-4 border-t border-gray-100 pt-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
              <UserGroupIcon className="h-4 w-4 text-gray-500" />
              Audience segment
              {campaign.audienceSegments.length === 1 ? "" : "s"}
            </div>
            {campaign.audienceSegments.length === 0 ? (
              <p className="text-sm text-gray-500">
                No audience segment is attached to this campaign yet.
              </p>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {campaign.audienceSegments.map((segment) => (
                  <div
                    key={segment.id}
                    className="rounded-md border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {segment.name}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {segment.isDynamic ? "Dynamic" : "Static"} segment
                          {!segment.sourceAvailable && " · archived provenance"}
                        </p>
                      </div>
                      {segment.sourceAvailable && (
                        <Link
                          to={`/segments?segment=${encodeURIComponent(segment.id)}`}
                          className="shrink-0 text-xs font-medium text-primary-700 hover:text-primary-900"
                        >
                          View
                        </Link>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      {segment.currentCount !== null && (
                        <span>
                          Current segment:{" "}
                          {segment.currentCount.toLocaleString()}
                        </span>
                      )}
                      {segment.frozenCount !== null && (
                        <span>
                          Frozen audience:{" "}
                          {segment.frozenCount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
